import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent } from 'react';
import { DateUtils } from 'record-it-utils/utils/DateUtils/DateUtils';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useFixedLengthNumberInput } from '../../hooks/useFixedLengthNumberInput/useFixedLengthNumberInput';
import { useMultiElementIsFocused } from '../../hooks/useMultiElementIsFocused/useMultiElementIsFocused';
import { InputLayout, InputLayoutProps } from '../../input/InputLayout/InputLayout';
import sharedStyles from '../../input/sharedStyles.module.css';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import styles from './DateInput.module.css';
export type DateInputProps = PropsWithoutChildren<Omit<InputLayoutProps, 'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture'>> & {
  value: Date | null;
  onInput: (value: Date | null) => void;
  onInputFinished?: () => void;
};
export const DateInput: FunctionComponent<DateInputProps> = React.memo(props => {
  const $ = _c(31);
  const {
    value,
    onInput,
    onInputFinished,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    isFocused: anyInputIsFocused,
    focusHandlerFactory,
    blurHandlerFactory
  } = useMultiElementIsFocused({
    onBlur: () => {
      let bufferedDate = null;
      if (dayInputBufferedValue != null && monthInputBufferedValue != null && yearInputBufferedValue != null) {
        bufferedDate = DateUtils.withCurrentTimezone().createDateFromParts({
          year: yearInputBufferedValue,
          month: monthInputBufferedValue,
          day: dayInputBufferedValue,
          hours: value?.getHours() ?? 12,
          minutes: value?.getMinutes() ?? 0,
          seconds: value?.getSeconds() ?? 0,
          milliseconds: value?.getMilliseconds() ?? 0
        });
      }
      if (bufferedDate?.getTime() !== value?.getTime()) {
        onInput(bufferedDate);
      }
    }
  });
  const {
    inputProps: yearInputProps,
    bufferedValue: t0,
    focus: focusYearInput
  } = useFixedLengthNumberInput({
    value: value?.getFullYear() ?? null,
    anyInputIsFocused,
    valueLength: 4,
    onValueLengthReached: onInputFinished,
    onFocus: focusHandlerFactory("YearInput"),
    onBlur: blurHandlerFactory("YearInput")
  });
  const yearInputBufferedValue = t0;
  let t1;
  if ($[0] !== focusYearInput) {
    t1 = () => {
      focusYearInput();
    };
    $[0] = focusYearInput;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleMonthInputValueLengthReached = useEventCallback(t1);
  const t2 = value ? value.getMonth() + 1 : null;
  let t3;
  if ($[2] !== focusHandlerFactory) {
    t3 = focusHandlerFactory("MonthInput");
    $[2] = focusHandlerFactory;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== blurHandlerFactory) {
    t4 = blurHandlerFactory("MonthInput");
    $[4] = blurHandlerFactory;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== anyInputIsFocused || $[7] !== handleMonthInputValueLengthReached || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      value: t2,
      anyInputIsFocused,
      valueLength: 2,
      onValueLengthReached: handleMonthInputValueLengthReached,
      onFocus: t3,
      onBlur: t4
    };
    $[6] = anyInputIsFocused;
    $[7] = handleMonthInputValueLengthReached;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    inputProps: monthInputProps,
    bufferedValue: t6,
    focus: focusMonthInput
  } = useFixedLengthNumberInput(t5);
  const monthInputBufferedValue = t6;
  let t7;
  if ($[12] !== focusMonthInput) {
    t7 = () => {
      focusMonthInput();
    };
    $[12] = focusMonthInput;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleDayInputValueLengthReached = useEventCallback(t7);
  const t8 = value?.getDate() ?? null;
  let t9;
  if ($[14] !== focusHandlerFactory) {
    t9 = focusHandlerFactory("DayInput");
    $[14] = focusHandlerFactory;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== blurHandlerFactory) {
    t10 = blurHandlerFactory("DayInput");
    $[16] = blurHandlerFactory;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== anyInputIsFocused || $[19] !== handleDayInputValueLengthReached || $[20] !== t10 || $[21] !== t8 || $[22] !== t9) {
    t11 = {
      value: t8,
      anyInputIsFocused,
      valueLength: 2,
      onValueLengthReached: handleDayInputValueLengthReached,
      onFocus: t9,
      onBlur: t10
    };
    $[18] = anyInputIsFocused;
    $[19] = handleDayInputValueLengthReached;
    $[20] = t10;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const {
    inputProps: dayInputProps,
    bufferedValue: t12
  } = useFixedLengthNumberInput(t11);
  const dayInputBufferedValue = t12;
  let t13;
  if ($[24] !== dayInputProps || $[25] !== monthInputProps || $[26] !== yearInputProps) {
    t13 = () => <div className={styles.inputsWrapper}><input type="text" className={classes(sharedStyles.inputBaseStyle, styles.input)} {...dayInputProps} data-testid="DateInput.DayInput" /><div className={styles.dateSeparator}>.</div><input type="text" className={classes(sharedStyles.inputBaseStyle, styles.input)} {...monthInputProps} data-testid="DateInput.MonthInput" /><div className={styles.dateSeparator}>.</div><input type="text" className={classes(sharedStyles.inputBaseStyle, styles.input)} {...yearInputProps} data-testid="DateInput.YearInput" /></div>;
    $[24] = dayInputProps;
    $[25] = monthInputProps;
    $[26] = yearInputProps;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== remainingProps || $[29] !== t13) {
    t14 = <InputLayout {...remainingProps}>{t13}</InputLayout>;
    $[28] = remainingProps;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
});